import { IChallengeListProductItem, IChallengesPartial } from 'interfaces';
import { GOAL } from 'constants/challenges';
import { WEB_FILES_PATHNAME } from '../constants';
import { CONTENT_BLOC } from 'constants/content';
import {
  EventStatus,
  getPublicationStatusFromDate,
} from 'core/utils/date.helpers_ts';
import DOMPurify from 'dompurify';


export { getExtension, getMediaObjectURI, getTypeFromExtension } from './media';

export const normalize = (
  data: { id: number }[],
): {
  allIds: number[];
  byId: { [id: number]: unknown };
} => {
  const allIds: number[] = [];
  const byId: { [id: number]: unknown } = {};

  data.forEach((item) => {
    allIds.push(item.id);
    byId[item.id] = item;
  });

  return { allIds, byId };
};

export const sumAll = (list?: number[]) =>
  list?.reduce((partialSum, number) => partialSum + number, 0) || 0;

export const getProductFieldId = (product: IChallengeListProductItem) =>
  product.challengeProduct['@id'];

export const isSameHostname = (url: string) =>
  new URL(url).hostname === new URL(window.location.href).hostname;

export const getResultUnit = (challenge: IChallengesPartial) =>
  (challenge.goalType === GOAL.RESULT
    ? challenge.resultUnit
    : challenge.challengeGoals?.[0]?.unit) ?? '';

export const isWebFileUrl = (url: string) => {
  return (
    isSameHostname(url) && new URL(url).pathname.startsWith(WEB_FILES_PATHNAME)
  );
};

// check if the content blocs list only contains widgets content and if all of these widgets aren't in running status
export const checkHasOnlyNotRunningWidgetsBlocs = (blocs: any) => {
  let hasOnlyWidgetsBlocs = true;
  let allWidgetHaveIncommingOrEndedStatus = true;
  for (let i = 0; i < blocs.length; i++) {
    const widgetStatus = getPublicationStatusFromDate(
      blocs[i].widget?.diffusionStartAt,
      blocs[i].widget?.diffusionEndAt,
    );
    if (blocs[i]['@type'] !== CONTENT_BLOC.WIDGET) {
      hasOnlyWidgetsBlocs = false;
      break;
    }
    if (
      blocs[i]['@type'] === CONTENT_BLOC.WIDGET &&
      widgetStatus === EventStatus.RUNNING
    ) {
      allWidgetHaveIncommingOrEndedStatus = false;
      break;
    }
  }
  return hasOnlyWidgetsBlocs && allWidgetHaveIncommingOrEndedStatus;
};

const ALLOWED_TAGS = [
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'p',
  'strong',
  'em',
  'u',
  'a',
  'br',
  'ul',
  'ol',
  'li',
  'img',
  'figure',
  'figcaption',
  'blockquote',
  'code',
  'pre',
  'sub',
  'sup',
  'hr',
  'table',
  'thead',
  'tbody',
  'tfoot',
  'tr',
  'th',
  'td',
  'col',
  'colgroup',
  'caption',
  'span',
  'div',
  'small',
  'mark',
  'del',
  'ins',
  'cite',
  'time',
  'abbr',
  'dfn',
  'kbd',
  'samp',
  'var',
  'bdi',
  'bdo',
  'wbr',
];


export const getSafeHTML = (dirtyHTML: string | HTMLDivElement) =>
  DOMPurify.sanitize(dirtyHTML, {
    ALLOWED_TAGS,
    ALLOWED_ATTR: ['href', 'target', 'rel', 'src', 'style'],
  });
